/**
 * Follower Card Component
 * Displays a single follower with avatar, info, and action buttons
 * Used in FollowersTabContent
 */

import { EllipsisVertical, MessageCircle } from "lucide-react"
import { useEffect, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { toast } from "sonner"
import { HeartFilledIcon, HeartOutlineIcon, UserProfileIcon, WarningIcon } from "@/components/icons"
import { ReportUserModal } from "@/components/shared/ReportUserModal"
import { Avatar } from "@/components/ui/avatar"
import { LoginModal } from "@/features/auth/components/LoginModal"
import { useAuthStore } from "@/features/auth/store/authStore"
import { useCreateDmConversation } from "@/features/chats/hooks/useDmChat"
import { useFollowActions } from "../hooks/useFollowActions"
import type { FollowItem } from "../services/follow.service"

interface FollowerCardProps {
  item: FollowItem
  currentUserId?: string
}

function getUserDisplayImage(item: FollowItem): string | null {
  if (item.user.profile_photos && item.user.profile_photos.length > 0) {
    const coverPhoto = item.user.profile_photos.find(p => p.isCover || p.is_cover_image)
    if (coverPhoto?.filePath) {
      return coverPhoto.filePath
    }
  }
  return item.user.profilePictureUrl
}

export function FollowerCard({ item, currentUserId }: FollowerCardProps) {
  const navigate = useNavigate()
  const menuRef = useRef<HTMLDivElement>(null)
  const [openMenu, setOpenMenu] = useState(false)
  const [showReportModal, setShowReportModal] = useState(false)
  const [showLoginModal, setShowLoginModal] = useState(false)
  const currentUser = useAuthStore(state => state.user)
  const isGuest = !currentUser
  const [followStatus, setFollowStatus] = useState(
    item.user.isFollowing !== undefined ? item.user.isFollowing : false,
  )

  const { followAsync, unfollowAsync, isFollowing, isUnfollowing } = useFollowActions()
  const createDmConversation = useCreateDmConversation()

  // Close menu on outside click
  useEffect(() => {
    if (!openMenu) return
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpenMenu(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [openMenu])

  const handleMenuButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    setOpenMenu(prev => !prev)
  }

  const handleProfileClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    setOpenMenu(false)
    navigate(`/profile/${item.user.id}`)
  }

  const handleReport = (e: React.MouseEvent) => {
    e.stopPropagation()
    setOpenMenu(false)
    if (isGuest) {
      setShowLoginModal(true)
      return
    }
    setShowReportModal(true)
  }

  const handleFollowToggle = async (e: React.MouseEvent) => {
    e.stopPropagation()
    if (isGuest) {
      setShowLoginModal(true)
      return
    }
    const wasFollowing = followStatus
    setFollowStatus(!wasFollowing)
    try {
      if (wasFollowing) {
        await unfollowAsync(item.user.id)
      } else {
        await followAsync(item.user.id)
      }
    } catch {
      setFollowStatus(wasFollowing)
    }
  }

  const handleChatClick = async (e: React.MouseEvent) => {
    e.stopPropagation()
    if (isGuest) {
      setShowLoginModal(true)
      return
    }
    if (createDmConversation.isPending) return
    try {
      const conversation = await createDmConversation.mutateAsync(item.user.id)
      navigate(`/chats/dm/${conversation.id}`)
    } catch {
      toast.error("Failed to start conversation")
    }
  }

  const handleCardClick = () => {
    navigate(`/profile/${item.user.id}`)
  }

  const handleCardKeyDown = (e: React.KeyboardEvent) => {
    // Match anchor semantics: Enter/Space activates the card. Don't
    // intercept when focus is in a descendant button — those handle
    // their own keyboard activation and would otherwise both fire.
    if (e.target !== e.currentTarget) return
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault()
      navigate(`/profile/${item.user.id}`)
    }
  }

  const displayImage = getUserDisplayImage(item)

  // Don't show actions for current user
  const isCurrentUser = item.user.id === currentUserId

  return (
    <>
      {/* Use a clickable div, not a <Link>. A <Link> renders as an <a>, and a
        descendant <button> click triggers the anchor's default navigation —
        e.stopPropagation() in React handlers doesn't stop browser-level
        anchor activation. This is what caused the chat / heart / menu icons
        to navigate to the profile instead of running their own handlers. */}
      <div
        role="button"
        tabIndex={0}
        onClick={handleCardClick}
        onKeyDown={handleCardKeyDown}
        aria-label={`View profile of ${item.user.name || item.user.username || "user"}`}
        className="relative flex items-center justify-between gap-3 md:rounded-xl border-t last:border-b md:border border-card bg-transparent md:bg-card py-3.75 md:p-5 transition-colors hover:bg-muted/50 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <div className="flex items-center gap-3.75">
          {/* Avatar */}
          <Avatar
            src={displayImage ?? undefined}
            fallback={item.user.name || item.user.username || "?"}
            size="lg"
            className="h-14 w-14"
          />

          {/* User Info */}
          <div className="flex flex-col min-w-0">
            <p className="truncate text-sm font-medium">{item.user.name || "No name"}</p>
            <p className="truncate text-xs text-muted-foreground">
              {item.user.username ? `@${item.user.username}` : "No username"}
            </p>
          </div>
        </div>

        {/* Action buttons */}
        {!isCurrentUser && (
          <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
            {/* Chat icon — opens (or reuses) a DM with this user and
              navigates to the conversation. */}
            <button
              type="button"
              className="p-1.5 rounded-full hover:bg-muted/50 cursor-pointer disabled:opacity-50"
              onClick={handleChatClick}
              disabled={createDmConversation.isPending}
              title="Chat"
              aria-label={`Chat with ${item.user.name || item.user.username || "user"}`}
            >
              <MessageCircle className="h-5 w-5 text-muted-foreground" />
            </button>

            {/* Follow button */}
            <button
              type="button"
              className="p-1.5 rounded-full hover:bg-muted/50 cursor-pointer"
              onClick={handleFollowToggle}
              disabled={isFollowing || isUnfollowing}
              title={followStatus ? "Unfollow" : "Follow"}
            >
              {followStatus ? (
                <HeartFilledIcon width={21} height={21} color="#919366" />
              ) : (
                <HeartOutlineIcon width={21} height={21} color="#F2F0EB" />
              )}
            </button>

            {/* Menu button */}
            <div className="relative" ref={menuRef}>
              <button
                type="button"
                className="p-1.5 rounded-full hover:bg-muted/50 cursor-pointer"
                onClick={handleMenuButtonClick}
              >
                <EllipsisVertical className="h-5 w-5 text-muted-foreground" />
              </button>

              {/* Dropdown */}
              {openMenu && (
                <div className="absolute right-0 p-4 bottom-10 w-80 bg-popover text-popover-foreground rounded-lg shadow-lg border border-border overflow-visible z-50 space-y-0">
                  <button
                    type="button"
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-muted/50 transition-colors cursor-pointer"
                    onClick={handleProfileClick}
                  >
                    <UserProfileIcon width={16} height={20} color="#F2F0EB" />
                    View Profile
                  </button>

                  <button
                    type="button"
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left text-brand-terracotta transition-colors cursor-pointer"
                    onClick={handleReport}
                  >
                    <WarningIcon width={18} height={19} color="#C86757" />
                    Report
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Report modal must live OUTSIDE the clickable card div to prevent
        click events from propagating to the card's onClick (which navigates
        to the user's profile). The modal uses a Radix portal to render at
        document.body level, but keeping it outside the card div eliminates
        any edge-case event propagation on mobile/touch devices. */}
      {showReportModal && (
        <ReportUserModal
          open={showReportModal}
          onOpenChange={setShowReportModal}
          user={{
            id: item.user.id,
            name: item.user.name || "User",
            username: item.user.username || "",
            profilePictureUrl: displayImage,
          }}
        />
      )}

      <LoginModal open={showLoginModal} onOpenChange={setShowLoginModal} />
    </>
  )
}
